import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Minus, QrCode, Cookie, Warning, Sparkle, ArrowRight, Storefront, X, ChartLineUp, TrendUp, TrendDown, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

// Bone-ivory + deep forest green + single cherry-red accent — Scandi bistro
const CREAM = "#F5F1E8";
const INK = "#0F1A14";
const AMBER = "#1F3A2A";
const ORANGE = "#C6392E";

const ALLERGENS = ["Gluten", "Dairy", "Nuts", "Egg", "Soy", "Sesame"];
const CATEGORIES = ["Pastry", "Cake", "Savoury", "Drink"];

const PRODUCT_DB = [
  { id: "p1", name: "Cinnamon Bun", category: "Pastry", price: 3.5, allergens: ["Gluten", "Dairy", "Egg"], emoji: "🥯" },
  { id: "p2", name: "Sourdough Loaf", category: "Savoury", price: 6.0, allergens: ["Gluten"], emoji: "🍞" },
  { id: "p3", name: "Flat White", category: "Drink", price: 3.2, allergens: ["Dairy"], emoji: "☕" },
  { id: "p4", name: "Lemon Drizzle Slice", category: "Cake", price: 3.8, allergens: ["Gluten", "Dairy", "Egg"], emoji: "🍋" },
  { id: "p5", name: "Sausage Roll", category: "Savoury", price: 4.5, allergens: ["Gluten", "Dairy", "Egg"], emoji: "🥐" },
  { id: "p6", name: "Oat Latte", category: "Drink", price: 3.4, allergens: [], emoji: "🥛" },
  { id: "p7", name: "Pecan Brownie", category: "Cake", price: 3.9, allergens: ["Gluten", "Dairy", "Egg", "Nuts"], emoji: "🍫" },
];

const TABS = [
  { key: "board", label: "Today's board" },
  { key: "products", label: "Products" },
  { key: "public", label: "Public menu" },
  { key: "analytics", label: "Analytics" },
];

export default function PopupPrep() {
  const [tab, setTab] = useState("board");
  const navigate = useNavigate();
  // Today's board: { productId: { stock, starting } }
  const [board, setBoard] = useState({
    p1: { stock: 8, starting: 12 },
    p3: { stock: 18, starting: 25 },
    p4: { stock: 0, starting: 8 },
    p6: { stock: 15, starting: 20 },
  });
  const [special, setSpecial] = useState(null); // { name, price, stock, until }

  const updateStock = (id, delta) => {
    setBoard((b) => {
      const curr = b[id]?.stock ?? 0;
      const next = Math.max(0, curr + delta);
      return { ...b, [id]: { ...b[id], stock: next } };
    });
  };

  const addProductToBoard = (p, starting) => {
    setBoard((b) => ({ ...b, [p.id]: { stock: starting, starting } }));
    toast.success(`${p.name} on the board (${starting} to start)`);
  };

  const removeFromBoard = (id) => {
    setBoard((b) => { const n = { ...b }; delete n[id]; return n; });
  };

  const boardItems = Object.entries(board).map(([id, v]) => ({ product: PRODUCT_DB.find((p) => p.id === id), ...v })).filter((x) => x.product);
  const soldToday = boardItems.reduce((t, i) => t + (i.starting - i.stock), 0);
  const totalStarting = boardItems.reduce((t, i) => t + i.starting, 0);

  return (
    <div className="min-h-screen" style={{ backgroundColor: CREAM, color: INK, fontFamily: "'Outfit', sans-serif" }} data-testid="popup-prep-interactive">
      <header className="sticky top-0 z-40 border-b" style={{ backgroundColor: "#FFFCF4", borderColor: "#F3E8D5" }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/demos/popup-prep" className="mr-1" style={{ color: INK, opacity: 0.5 }}><ArrowLeft size={18} /></Link>
            <div className="h-9 w-9 rounded-full flex items-center justify-center text-xl" style={{ background: `linear-gradient(135deg, ${AMBER}, ${ORANGE})` }}>
              <Cookie size={18} weight="fill" color="white" />
            </div>
            <div>
              <div className="text-base font-bold leading-none">Birch & Bean <span className="font-mono text-[10px] opacity-60">· Concept mock</span></div>
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mt-0.5">Saturday market · one-click load-in</div>
            </div>
          </div>
          <button onClick={() => navigate("/consultation?demo=popup-prep")} className="text-xs font-semibold px-3 py-1.5 text-white whitespace-nowrap" style={{ backgroundColor: ORANGE }}>
            Want one for your café? <ArrowRight size={12} weight="bold" className="inline ml-1" />
          </button>
        </div>
        <nav className="max-w-6xl mx-auto px-6 flex gap-6 border-t" style={{ borderColor: "#F3E8D5" }}>
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className="relative py-3 text-sm font-medium" style={{ color: tab === t.key ? INK : "#78716C" }} data-testid={`popup-tab-${t.key}`}>
              {t.label}
              {tab === t.key && <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: AMBER }} />}
            </button>
          ))}
        </nav>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-8">
        {tab === "board" && (
          <BoardTab items={boardItems} updateStock={updateStock} removeFromBoard={removeFromBoard} special={special} setSpecial={setSpecial} soldToday={soldToday} totalStarting={totalStarting} />
        )}
        {tab === "products" && (
          <ProductsTab board={board} onAdd={addProductToBoard} />
        )}
        {tab === "public" && (
          <PublicMenuTab items={boardItems} special={special} />
        )}
        {tab === "analytics" && (
          <AnalyticsTab />
        )}
      </section>

      <footer className="bg-white/50 border-t mt-8 py-10" style={{ borderColor: "#F3E8D5" }}>
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-3">Concept · interactive mock</p>
          <p className="max-w-md mx-auto text-sm opacity-80 mb-5">If this shape fits your café, we can build a real one in 3–4 weeks — connected to your POS so counts stay honest.</p>
          <Link to="/consultation?demo=popup-prep" className="inline-flex items-center gap-2 text-white px-5 py-3 text-sm font-semibold" style={{ backgroundColor: ORANGE }}>Talk to us about a build like this <ArrowRight size={14} weight="bold" /></Link>
        </div>
      </footer>
    </div>
  );
}

function BoardTab({ items, updateStock, removeFromBoard, special, setSpecial, soldToday, totalStarting }) {
  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-6">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-1">Saturday · 09:40</p>
          <h1 className="text-4xl font-black tracking-tight">Today's board</h1>
        </div>
        <div className="flex gap-5 text-sm">
          <div><div className="text-2xl font-black" style={{ color: ORANGE }}>{soldToday}</div><div className="text-[10px] font-mono uppercase tracking-widest opacity-60">Sold</div></div>
          <div><div className="text-2xl font-black">{totalStarting - soldToday}</div><div className="text-[10px] font-mono uppercase tracking-widest opacity-60">Remaining</div></div>
          <div><div className="text-2xl font-black">{totalStarting}</div><div className="text-[10px] font-mono uppercase tracking-widest opacity-60">Started with</div></div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((i) => {
          const pct = Math.max(0, Math.min(100, (i.stock / i.starting) * 100));
          const sold = i.starting - i.stock;
          return (
            <article key={i.product.id} className="bg-white border p-5 flex gap-4 items-center" style={{ borderColor: "#F3E8D5" }} data-testid={`popup-item-${i.product.id}`}>
              <div className="text-4xl flex-shrink-0 w-12 text-center">{i.product.emoji}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-bold text-lg">{i.product.name}</h3>
                    <p className="text-xs opacity-60 font-mono">£{i.product.price.toFixed(2)} · {i.product.category}</p>
                  </div>
                  <button onClick={() => removeFromBoard(i.product.id)} className="opacity-40 hover:opacity-100 text-xs"><X size={14} /></button>
                </div>
                <div className="mt-2.5 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "#F3E8D5" }}>
                  <div className="h-full rounded-full transition-all" style={{ width: pct + "%", backgroundColor: i.stock === 0 ? "#DC2626" : pct < 25 ? ORANGE : AMBER }} />
                </div>
                <div className="flex items-center justify-between mt-2 text-xs">
                  <span className="font-mono">{i.stock === 0 ? <span className="text-red-700 font-bold">SOLD OUT</span> : <span><b>{i.stock}</b> left · <span className="opacity-60">{sold} sold</span></span>}</span>
                  <div className="flex gap-1">
                    <button onClick={() => updateStock(i.product.id, -1)} className="w-7 h-7 border rounded flex items-center justify-center hover:bg-zinc-50" style={{ borderColor: "#E7D5B8" }} data-testid={`popup-minus-${i.product.id}`}><Minus size={12} /></button>
                    <button onClick={() => updateStock(i.product.id, 1)} className="w-7 h-7 border rounded flex items-center justify-center hover:bg-zinc-50" style={{ borderColor: "#E7D5B8" }} data-testid={`popup-plus-${i.product.id}`}><Plus size={12} /></button>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <div className="mt-6 p-5 border border-dashed" style={{ borderColor: "#E7D5B8", backgroundColor: "#FFFCF4" }} data-testid="popup-special">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-bold flex items-center gap-2"><Sparkle size={14} weight="fill" style={{ color: ORANGE }} /> Today's special</p>
          {special && <button onClick={() => setSpecial(null)} className="text-xs opacity-60 hover:opacity-100">Remove</button>}
        </div>
        {special ? (
          <div><b>{special.name}</b> — £{special.price.toFixed(2)} · {special.stock} available until {special.until}</div>
        ) : (
          <button onClick={() => setSpecial({ name: "Pecan Brownie + Oat Latte", price: 6.5, stock: 15, until: "12:00" })} className="text-sm opacity-70 hover:opacity-100 underline" data-testid="popup-add-special">+ Add today's special in 1 tap</button>
        )}
      </div>
    </div>
  );
}

function ProductsTab({ board, onAdd }) {
  return (
    <div>
      <h1 className="text-4xl font-black tracking-tight mb-2">Product database</h1>
      <p className="text-sm opacity-70 mb-6 max-w-xl">Kept once, reused every day. Each product carries its name, category, price, allergens and photo — so the EHO allergen check is ready when they turn up.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {PRODUCT_DB.map((p) => {
          const onBoard = !!board[p.id];
          return (
            <article key={p.id} className="bg-white border p-5" style={{ borderColor: "#F3E8D5" }} data-testid={`popup-prod-${p.id}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="text-3xl w-10 text-center">{p.emoji}</div>
                <div className="flex-1">
                  <h3 className="font-bold">{p.name}</h3>
                  <p className="text-xs opacity-60 font-mono">{p.category} · £{p.price.toFixed(2)}</p>
                </div>
                {p.allergens.length > 0 && <Warning size={16} weight="fill" className="text-amber-600" />}
              </div>
              {p.allergens.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {p.allergens.map((a) => <span key={a} className="text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 bg-amber-50 border-amber-200 text-amber-900">{a}</span>)}
                </div>
              )}
              {onBoard ? (
                <div className="text-xs font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2">✓ On today's board</div>
              ) : (
                <button onClick={() => onAdd(p, 10)} className="w-full text-xs font-semibold text-white py-2" style={{ backgroundColor: INK }} data-testid={`popup-add-${p.id}`}>
                  Add to today — start with 10
                </button>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}

function PublicMenuTab({ items, special }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <QrCode size={32} weight="fill" style={{ color: AMBER }} />
        <div>
          <h1 className="text-3xl font-black tracking-tight">Public menu preview</h1>
          <p className="text-sm opacity-70">This is what customers see when they scan the QR in your window — no app install.</p>
        </div>
      </div>
      <div className="mx-auto max-w-md border-4 rounded-3xl p-6" style={{ borderColor: INK, backgroundColor: "white" }}>
        <div className="text-center mb-5 pb-5 border-b" style={{ borderColor: "#F3E8D5" }}>
          <p className="text-xs font-mono uppercase tracking-widest opacity-60">Birch & Bean · Today</p>
          <h2 className="text-2xl font-black">On the board</h2>
          <p className="text-xs font-mono opacity-60 mt-1">Live · updated every time we ring a sale</p>
        </div>
        <ul className="space-y-3">
          {items.map((i) => (
            <li key={i.product.id} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-xl">{i.product.emoji}</span>
                <div>
                  <div className={`font-semibold ${i.stock === 0 ? "line-through opacity-50" : ""}`}>{i.product.name}</div>
                  <div className="text-xs opacity-60 font-mono">£{i.product.price.toFixed(2)}{i.product.allergens.length ? " · ⚠ " + i.product.allergens.join(", ") : ""}</div>
                </div>
              </div>
              <span className="text-xs font-mono font-bold" style={{ color: i.stock === 0 ? "#DC2626" : i.stock < 4 ? ORANGE : "#0F766E" }}>
                {i.stock === 0 ? "SOLD OUT" : i.stock < 4 ? `${i.stock} left` : "In stock"}
              </span>
            </li>
          ))}
          {special && (
            <li className="flex items-center justify-between pt-3 border-t" style={{ borderColor: "#F3E8D5" }}>
              <div className="flex items-center gap-3">
                <Sparkle size={18} weight="fill" style={{ color: ORANGE }} />
                <div>
                  <div className="font-semibold">{special.name}</div>
                  <div className="text-xs opacity-60 font-mono">£{special.price.toFixed(2)} · until {special.until}</div>
                </div>
              </div>
              <span className="text-xs font-mono font-bold" style={{ color: ORANGE }}>SPECIAL</span>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

function AnalyticsTab() {
  // Mock week data — Mon..Sat takings in £
  const week = [
    { day: "Mon", takings: 184, items: 62 },
    { day: "Tue", takings: 142, items: 51 },
    { day: "Wed", takings: 201, items: 74 },
    { day: "Thu", takings: 178, items: 65 },
    { day: "Fri", takings: 266, items: 94 },
    { day: "Sat", takings: 348, items: 124 },
  ];
  const maxTakings = Math.max(...week.map((d) => d.takings));
  const weekTotal = week.reduce((t, d) => t + d.takings, 0);
  const itemsTotal = week.reduce((t, d) => t + d.items, 0);
  const avgBasket = (weekTotal / itemsTotal) * 1.8; // fictional basket inflation

  // Mock product performance
  const topSellers = [
    { name: "Cinnamon Bun", sold: 68, revenue: 238, emoji: "🥯", trend: "+18%" },
    { name: "Oat Latte", sold: 54, revenue: 184, emoji: "🥛", trend: "+4%" },
    { name: "Sourdough Loaf", sold: 32, revenue: 192, emoji: "🍞", trend: "+12%" },
    { name: "Pecan Brownie", sold: 24, revenue: 94, emoji: "🍫", trend: "–3%" },
    { name: "Sausage Roll", sold: 18, revenue: 81, emoji: "🥐", trend: "+2%" },
  ];

  // Hour-of-day heat (0 = quiet, 4 = rammed)
  const hours = ["07", "08", "09", "10", "11", "12", "13", "14"];
  const heat = [2, 4, 4, 3, 2, 3, 3, 1];

  // Sold-out signal
  const soldOuts = [
    { name: "Lemon Drizzle", day: "Sat", soldOutAt: "11:20", demand: "Kept selling" },
    { name: "Sausage Roll", day: "Fri", soldOutAt: "13:45", demand: "Light" },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-6">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-1">Owner view · last 6 days</p>
          <h1 className="text-4xl font-black tracking-tighter">Analytics</h1>
        </div>
        <div className="flex gap-6 text-right">
          <StatBlock label="Takings" value={`£${weekTotal}`} sub="+14% vs last week" up />
          <StatBlock label="Items sold" value={itemsTotal} sub={`Avg basket £${avgBasket.toFixed(2)}`} />
          <StatBlock label="Sell-through" value="87%" sub="Below 10% waste" up />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        {/* Takings chart */}
        <div className="lg:col-span-2 bg-white border p-5" style={{ borderColor: "#E8DFC9" }} data-testid="popup-analytics-chart">
          <div className="flex items-baseline justify-between mb-5">
            <h2 className="text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>Daily takings</h2>
            <span className="text-[10px] font-mono uppercase tracking-widest opacity-60">Mon–Sat · this week</span>
          </div>
          <div className="flex items-end gap-3 h-40">
            {week.map((d) => {
              const pct = (d.takings / maxTakings) * 100;
              const isBest = d.takings === maxTakings;
              return (
                <div key={d.day} className="flex-1 flex flex-col items-center gap-1 justify-end h-full">
                  <div className="text-[10px] font-bold font-mono opacity-80">£{d.takings}</div>
                  <div className="w-full flex items-end" style={{ height: "70%" }}>
                    <div className="w-full transition-all" style={{ height: `${pct}%`, background: isBest ? ORANGE : AMBER }} />
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mt-1">{d.day}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-3 border-t flex items-center gap-2 text-xs" style={{ borderColor: "#F3EBD5" }}>
            <Sparkle size={12} weight="fill" style={{ color: ORANGE }} />
            <span><b>Saturday</b> is your busiest day — £348 taken, 124 items. Keep the dough going.</span>
          </div>
        </div>

        {/* Hour-of-day heat */}
        <div className="bg-white border p-5" style={{ borderColor: "#E8DFC9" }} data-testid="popup-analytics-heat">
          <div className="flex items-baseline justify-between mb-5">
            <h2 className="text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>Peak hours</h2>
            <span className="text-[10px] font-mono uppercase tracking-widest opacity-60">Saturday</span>
          </div>
          <div className="flex items-end gap-1 h-32">
            {heat.map((h, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
                <div className="w-full rounded-sm" style={{ height: `${(h / 4) * 100}%`, background: h >= 4 ? ORANGE : h >= 3 ? AMBER : "#C7BFA0", opacity: h === 0 ? 0.3 : 1 }} />
                <div className="text-[9px] font-mono opacity-60">{hours[i]}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs">
            <Clock size={12} weight="fill" style={{ color: AMBER }} />
            <span><b>8–10am</b> is rammed. Bake the bun batch for 07:30.</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Top sellers */}
        <div className="lg:col-span-2 bg-white border" style={{ borderColor: "#E8DFC9" }} data-testid="popup-analytics-top">
          <div className="px-5 py-3 border-b flex items-center gap-2" style={{ borderColor: "#F3EBD5" }}>
            <ChartLineUp size={14} weight="fill" style={{ color: AMBER }} />
            <h2 className="text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>Top sellers · this week</h2>
          </div>
          <ul>
            {topSellers.map((p, i) => {
              const trendUp = p.trend.startsWith("+");
              return (
                <li key={p.name} className="px-5 py-3 flex items-center gap-3 border-b last:border-b-0" style={{ borderColor: "#F3EBD5" }}>
                  <span className="text-[11px] font-mono font-bold opacity-50 w-4">{i + 1}</span>
                  <span className="text-xl">{p.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.name}</div>
                    <div className="text-[11px] font-mono opacity-60">{p.sold} sold · £{p.revenue}</div>
                  </div>
                  <div className="flex items-center gap-1 text-xs font-bold" style={{ color: trendUp ? AMBER : ORANGE }}>
                    {trendUp ? <TrendUp size={12} weight="bold" /> : <TrendDown size={12} weight="bold" />}
                    {p.trend}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Sold-out signals */}
        <div className="bg-white border p-5" style={{ borderColor: "#E8DFC9" }} data-testid="popup-analytics-sold-out">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>Sold out too early?</h2>
          </div>
          <ul className="space-y-3">
            {soldOuts.map((s, i) => (
              <li key={i} className="p-3 border-l-2" style={{ borderColor: s.demand === "Kept selling" ? ORANGE : "#C7BFA0", backgroundColor: s.demand === "Kept selling" ? "#FBF0EC" : "#F8F3E4" }}>
                <div className="flex items-baseline justify-between">
                  <div className="font-bold text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.name}</div>
                  <span className="text-[10px] font-mono opacity-60">{s.day}</span>
                </div>
                <div className="text-[11px] mt-0.5">Sold out {s.soldOutAt} · <span className="font-bold" style={{ color: s.demand === "Kept selling" ? ORANGE : INK }}>{s.demand}</span></div>
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-3 border-t text-xs" style={{ borderColor: "#F3EBD5" }}>
            <p className="opacity-70 italic">Next Saturday — <b>bake +20% Lemon Drizzle</b>. You left money on the counter last week.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatBlock({ label, value, sub, up }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">{label}</div>
      <div className="text-2xl font-black tracking-tighter mt-1" style={{ color: up ? AMBER : INK, fontFamily: "'Outfit', sans-serif" }}>{value}</div>
      <div className="text-[10px] font-mono opacity-60 mt-0.5">{sub}</div>
    </div>
  );
}

